import { isDeliveredOrderStatus, OrderStatusEnum } from '../../orders/table-cell/model/orderCell';
import type { PhotosetOrderStage } from '../model/orderStage';

export interface ShareGateInput {
  /** The photoset fetch is still in flight. */
  loading: boolean;
  selectedPhotosetId: string | null;
  /** The selected photoset's OWN order (`photoset.orderUUID`), the one
   *  asset-service judges when minting, never the workspace's. */
  orderStage: PhotosetOrderStage;
  /** From usePhotosetVisibility. `available` is false for anyone who cannot
   *  see the flag (customers: asset-service never lists a hidden set to them),
   *  so for them the selected photoset is visible by construction. */
  visibility: { available: boolean; visible: boolean };
  /** canSharePhotoset(role). */
  canShare: boolean;
}

/**
 * Why the gallery link cannot be switched on, if it can't. asset-service
 * refuses a mint (400) for a photoset with no order, an undelivered order or a
 * hidden photoset, so the switch is disabled rather than offered.
 *  - `no-order`: the photoset names no order.
 *  - `order-pending` / `order-unknown`: the order read is still out, or failed.
 *    Nothing is claimed about the order until it has been read.
 *  - `order-canceled` / `order-incomplete`: the order will never be delivered.
 *  - `not-delivered`: the order is not yet delivered or verified.
 *  - `hidden`: the photoset is hidden from the client.
 */
export type GalleryLinkBlock =
  | 'no-order'
  | 'order-pending'
  | 'order-unknown'
  | 'order-canceled'
  | 'order-incomplete'
  | 'not-delivered'
  | 'hidden';

export interface GalleryLinkGate {
  /** Why turning the link ON is refused, or null when it is not. */
  blockedBy: GalleryLinkBlock | null;
  /** The photoset is hidden from the client, so a live link serves "not
   *  available" and is offered only to be turned off. Kept apart from
   *  `blockedBy`, which names an order block first. */
  hiddenFromClient: boolean;
}

export type ShareGate =
  | { show: false }
  | { show: true; photosetId: string; galleryLink: GalleryLinkGate };

/**
 * Whether the Manage Assets header offers the Share control, and whether its
 * PlanOtour link can be switched on.
 *
 * The control shows for a loaded, selected photoset and a role that may share,
 * whatever the order's stage: Internal Share does not wait for delivery.
 * Turning the gallery link ON needs the photoset's own order delivered (or
 * verified) and a photoset the client can see; asset-service enforces both
 * again when minting (`requireDelivered` reads `photoset.orderUUID`), so this
 * is about not offering a switch the server would refuse. Turning it OFF is
 * never blocked: a live link must stay revocable.
 */
export const shareGate = ({
  loading,
  selectedPhotosetId,
  orderStage,
  visibility,
  canShare
}: ShareGateInput): ShareGate => {
  if (!canShare) return { show: false };
  if (loading || selectedPhotosetId === null) return { show: false };
  const hiddenFromClient = visibility.available && !visibility.visible;
  return {
    show: true,
    photosetId: selectedPhotosetId,
    galleryLink: {
      // The order first: making a set visible does not help an order that is
      // not delivered, so naming `hidden` first would send the user to clear
      // one block only to meet the next.
      blockedBy: orderBlock(orderStage) ?? (hiddenFromClient ? 'hidden' : null),
      hiddenFromClient
    }
  };
};

/**
 * The live link to offer for copying and opening, or null. A live link on a
 * photoset hidden from the client serves "not available", so it is offered
 * only to be turned off.
 */
export const shareableGalleryUrl = (url: string | null, gate: GalleryLinkGate): string | null =>
  gate.hiddenFromClient ? null : url;

const orderBlock = (stage: PhotosetOrderStage): GalleryLinkBlock | null => {
  if (stage.state === 'no-order') return 'no-order';
  if (stage.state === 'loading') return 'order-pending';
  if (stage.state === 'failed') return 'order-unknown';
  if (isDeliveredOrderStatus(stage.status)) return null;
  if (stage.status === OrderStatusEnum.CANCELLED) return 'order-canceled';
  if (stage.status === OrderStatusEnum.UNABLE_TO_COMPLETE) return 'order-incomplete';
  return 'not-delivered';
};
